import React, { useEffect, useRef, useState } from "react"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import {StyledDatePicker} from "./Input.styled"

const DatePickerSelectV2 = ({
    label,
    onChange,
    size = "md",
    value,
    maxDate,
    error = false,
    helperText,
    onDraftChange,
    commitOnAccept = false,
    defaultToToday = true,
    required = false,
    filledBackground = "primary",
}) => {
    const [date, setDate] = useState(defaultToToday ? dayjs() : null)
    const lastAcceptedDateRef = useRef(null)
    const maximumDate =
        maxDate && dayjs(maxDate).isValid() ? dayjs(maxDate) : undefined

    useEffect(() => {
        if (value && dayjs(value).isValid()) {
            const parsedDate = dayjs(value)
            setDate(parsedDate)
            lastAcceptedDateRef.current = parsedDate.valueOf()
        } else {
            setDate(defaultToToday ? dayjs() : null)
            lastAcceptedDateRef.current = null
        }
    }, [defaultToToday, value])

    const changeDate = (value) => {
        setDate(value)
        if (onDraftChange) onDraftChange(value)
        if (!commitOnAccept) onChange(value)
    }

    const commitDate = (value) => {
        if (!commitOnAccept) return

        const parsedDate = dayjs(value)
        if (!value || !parsedDate.isValid()) return
        if (lastAcceptedDateRef.current === parsedDate.valueOf()) return

        lastAcceptedDateRef.current = parsedDate.valueOf()
        onChange(value)
    }

    const handleKeyDownCapture = (event) => {
        if (!commitOnAccept || event.key !== "Enter") return

        event.preventDefault()
        commitDate(date)
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
                label={label || "Data"}
                value={date}
                maxDate={maximumDate}
                format="DD/MM/YYYY"
                onChange={changeDate}
                onAccept={commitDate}
                slots={{ textField: StyledDatePicker }}
                slotProps={{
                    textField: {
                        variant: "filled",
                        fullWidth: true,
                        pickerSize: size,
                        filledBackground,
                        error,
                        helperText,
                        required,
                        onKeyDownCapture: handleKeyDownCapture,
                    },
                }}
            />
        </LocalizationProvider>
    )
}

DatePickerSelectV2.propTypes = {
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    size: PropTypes.oneOf(["compact", "sm", "md", "xl"]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    error: PropTypes.bool,
    helperText: PropTypes.string,
    onDraftChange: PropTypes.func,
    commitOnAccept: PropTypes.bool,
    defaultToToday: PropTypes.bool,
    required: PropTypes.bool,
    filledBackground: PropTypes.oneOf(["primary", "white05"]),
}

export default DatePickerSelectV2
